import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import ProgressBar from "../helpers/progress_bar"
import OrderCalendarDrawer, { SimpleModal, buttonLoading, buttonReset, fetchJson } from "../helpers/order_calendar_drawer"

const NS = ".orderRecipients"

// Markup for the select-all dropdown injected into the table header on draw. Duplicated in
// contacts_controller.js, which renders the same dropdown. The two checkboxes carry their own
// data-action so Stimulus wires them up the moment this string lands in the DOM (its own
// MutationObserver watches for data-action/data-controller/target attributes regardless of how
// they arrive - a jQuery-inserted string included), the same mechanism patterns.md #3 documents
// for a server-rendered partial. That replaces the old click-delegation on the controller element.
const SELECT_ALL_DROPDOWN_HTML = `
  <div class="dropdown select-all btn-group">
    <button id="select-all-dropdown" class="btn-dropdown btn btn-borderless btn-transparent dropdown-toggle btn-sm" data-toggle="dropdown" type="button">
      <div class="form-group top-level">
        <input class="select-all" name="select-all" type="checkbox" aria-label="Select multiple elements">
        <span class="badge">(0)</span>
        <span class="caret"></span>
      </div>
    </button>
    <ul class="dropdown-menu">
      <li>
        <div class="form-group">
          <div class="checkbox inline">
            <input id="select-all-elements" class="boolean optional" type="checkbox" aria-label="Select all elements" data-action="click->order-recipients#onSelectAllOrPage">
            <label class="boolean optional" for="select-all-elements">All Recipients</label>
          </div>
        </div>
      </li>
      <li>
        <div class="form-group">
          <div class="checkbox inline">
            <input id="select-this-page" class="boolean optional" type="checkbox" aria-label="Select this page" data-action="click->order-recipients#onSelectAllOrPage">
            <label class="boolean optional" for="select-this-page">This Page</label>
          </div>
        </div>
      </li>
    </ul>
  </div>
`;

// Paired with the shared `datatable` controller on the same `#recipients-content` mount: that one
// owns the DataTables lifecycle and the standard reskin, this one owns the selection-state
// bookkeeping, the select-all-dropdown header injection and the filters. That extra work hangs off
// DataTables' own `draw.dt` jQuery event on the shared `table` target.
//
// The dataset reads below are deliberately not Values (see CONVENTIONS.md, "Why isn't this data
// attribute a Value?"): the "stages"/"stage"/"type"/"url" data attributes are each read off a
// specific gift-checkbox/table-action-button/drawer-trigger element, one value per element of a
// set with several - the Values API is scoped to the controller's own root element and cannot
// express "one value per matched element" (decisions.md D3). `data-source` on `tableTarget` is
// the shared read/write-on-the-table-target convention 15+ other datatable-driving controllers
// use verbatim (decisions.md D8).
export default class extends Controller {
  static get targets() { return ["table", "dateRangeInput", "giftStatusFilter"]; }
  static get values() { return { timezoneOffset: String }; }

  connect() {
    this.allStages = [];
    this.recipientsCount = 0;
    this.filters = { search: "", range: "", gift_status: [] };
    this.selections = { all: false, yes: [], no: [], stages: [] };

    // draw.dt is DataTables' own jQuery-announced event (not a native DOM event a browser ever
    // dispatches), so it can't be reached with a native addEventListener or a data-action - stays
    // delegated on jQuery's event bus, and onDraw needs to be pre-bound to this instance so
    // jQuery's call still resolves `this` correctly.
    this.onDraw = this.onDraw.bind(this);
    $(this.tableTarget).on(`draw.dt${NS}`, this.onDraw);

    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
    }, (start, end) => {
      this.reload({ range: `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}` });
    });

    $(this.giftStatusFilterTarget).select2({ placeholder: "Gift Status", width: "100%" });

    // The table body is redrawn by the DataTables plugin itself (not by this controller, and not
    // by a partial this controller also renders directly - see patterns.md #3), so these two stay
    // delegated on jQuery rather than becoming data-action: a listener bound directly to a row
    // checkbox would be gone the moment DataTables redraws the page.
    $(this.element).on(`change${NS}`, ".gift-checkbox", (event) => this.onGiftCheckboxChange(event));

    // select2 announces a pick via jQuery's `.trigger("change")`, not a native dispatch, so this
    // stays on jQuery's event bus rather than becoming a data-action.
    $(this.element).on(`change${NS}`, ".datatable-moveable-filter", () => this.reload());

    // No `select[name=per]` renders on this view today, so this binding is currently inert.
    $(this.element).on(`change${NS}`, "select[name=per]", (event) => {
      this.updatePerPage(parseInt($(event.currentTarget).val(), 10));
      this.reload();
    });
  }

  disconnect() {
    $(this.tableTarget).off(NS);
    $(this.element).off(NS);

    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) { picker.remove(); }

    if ($(this.giftStatusFilterTarget).data("select2")) { $(this.giftStatusFilterTarget).select2("destroy"); }
  }

  // Scoped-lookup helpers so an absent-element check stays a single no-op, the way jQuery's
  // scoped find() always was, instead of scattering `?.` guards at every call site (patterns.md
  // #1). Both intentionally query `this.element` directly - that's the one
  // querySelector/querySelectorAll call each, everywhere else in this controller goes through
  // them.
  eachIn(selector, callback) {
    this.element.querySelectorAll(selector).forEach(callback);
  }

  findIn(selector) {
    return this.element.querySelector(selector);
  }

  // jQuery's `draw.dt` handler signature is `(event, settings)`, where `settings` is the same
  // DataTables settings object `fnDrawCallback` would have received.
  onDraw(event, settings) {
    this.allStages = settings.json.allStages;
    this.recipientsCount = settings.fnRecordsDisplay();
    // The template is a hardcoded constant with zero interpolated data (see its own comment) -
    // safe by construction, not from a response or user input.
    this.tableTarget.querySelector("thead tr th:nth-child(2)").innerHTML = SELECT_ALL_DROPDOWN_HTML;
    this.resetSelections();
    this.toggleActionButtons();

    this.setFilters({
      search: settings.oPreviousSearch.sSearch,
      range: this.getDatatableRange(),
      gift_status: $(this.giftStatusFilterTarget).select2("val"),
    });
    this.updateCountBadge();
  }

  getDatatableRange() {
    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) {
      return `${picker.startDate.format("MMDDYYYY")}-${picker.endDate.format("MMDDYYYY")}`;
    }
  }

  setFilters(filters) {
    this.filters = {
      search: filters.search || "",
      range: filters.range || "",
      gift_status: filters.gift_status || "",
    };
  }

  checkedIds() {
    const checkedIds = [];
    this.eachIn(".gift-checkbox:checked", (el) => checkedIds.push(el.id.split("_")[1]));
    return checkedIds;
  }

  uncheckedIds() {
    const uncheckedIds = [];
    this.eachIn(".gift-checkbox:not(:checked)", (el) => uncheckedIds.push(el.id.split("_")[1]));
    return uncheckedIds;
  }

  addOrRemoveStage(checkedStage, add = true) {
    if (add) {
      this.selections.stages.push(checkedStage);
    } else {
      this.selections.stages.splice(this.selections.stages.findIndex((stage) => stage === checkedStage), 1);
    }
  }

  syncSelectedIds() {
    this.selections.yes = [];
    this.selections.no = [];

    if (this.selections.all) { this.selections.yes = ["all"]; }

    const checkedIds = this.checkedIds();
    const uncheckedIds = this.uncheckedIds();

    if (checkedIds.length > 0) {
      this.selections.no = this.selections.no.filter((x) => !checkedIds.includes(x));
      this.selections.no.push(...uncheckedIds);
      this.selections.no = [...new Set(this.selections.no)];
    }

    if (this.selections.all) { return; }

    this.selections.yes = [];
    this.selections.yes = this.selections.yes.filter((x) => !uncheckedIds.includes(x));
    this.selections.yes.push(...checkedIds);
    this.selections.yes = [...new Set(this.selections.yes)];
  }

  toggleActionButtons() {
    this.syncSelectedIds();

    this.eachIn(".table-actions .buttons button", (button) => {
      let selectedStages = [...new Set(this.selections.stages)];
      if (this.selections.all) { selectedStages = this.allStages; }

      const buttonStages = JSON.parse(button.dataset.stages);
      const difference = selectedStages.filter((x) => !buttonStages.includes(x));

      if (difference.length > 0) {
        button.disabled = true;
        button.parentElement.setAttribute("data-original-title", `Actions not available for ${difference.join(", ")} stages`);
      } else {
        button.disabled = this.selections.yes.length < 1;
        if (this.selections.yes.length < 1) {
          button.parentElement.setAttribute("data-original-title", "Select at least one gift");
        } else {
          button.parentElement.setAttribute("data-original-title", "");
        }
      }
    });

    this.updateCountBadge();
  }

  updateCountBadge() {
    let total = 0;
    if (this.selections.all) {
      total = this.recipientsCount - this.selections.no.length;
    } else {
      total = Math.min(this.recipientsCount, this.selections.yes.length);
    }

    const selectAllLabel = this.findIn("label[for=select-all-elements]");
    if (selectAllLabel) {
      const filtered = this.filters.search != "" || this.filters.gift_status.length > 0;
      selectAllLabel.textContent = filtered ? "All filtered recipients" : "All recipients";
    }

    const badge = this.findIn("#select-all-dropdown span.badge");
    if (badge) { badge.textContent = `(${total})`; }

    // Global id lookup: the modal is a sibling of `#recipients-content`, not a descendant, so it
    // sits outside `this.element`.
    const giftsCount = document.getElementById("gifts-delete-modal")?.querySelector("span.gifts-count");
    if (giftsCount) { giftsCount.textContent = `${total} gift${total == 1 ? "" : "s"}`; }
  }

  // Dead: nothing calls this.
  checkSelectedCheckboxes() {
    if (this.selections.all) {
      const selectAll = this.findIn("input.select-all");
      if (selectAll) { selectAll.checked = true; }
      const allElements = this.findIn("#select-all-elements");
      if (allElements) { allElements.checked = true; }
    }

    this.eachIn(".gift-checkbox:not(:checked)", (element) => {
      this.selections.yes.forEach((giftId) => {
        if (element.id === `gift_${giftId}`) { element.checked = true; }
      });

      if (this.selections.all) {
        element.checked = true;
        this.selections.no.forEach((deselectedGiftId) => {
          if (deselectedGiftId === element.id.split("_")[1]) { element.checked = false; }
        });
      }
    });

    this.updateCountBadge();
  }

  resetSelections() {
    this.eachIn("input[type='checkbox']", (checkbox) => { checkbox.checked = false; });
    this.selections.all = false;
    this.selections.yes = [];
    this.selections.no = [];
    this.toggleActionButtons();
  }

  // The two classList calls below are a new R4 finding, not a new problem: the original toggled
  // "indeterminate" through a stored jQuery handle (`$selectAllCheckbox.addClass(...)`), which
  // the checker can't see either way. Converting that handle's own lookup to native (this.findIn,
  // an R8 fix) forces the class toggle to go native too - decisions.md D2's R8-for-R4 trade,
  // reported rather than chased.
  onSelectAllOrPage(event) {
    const selectAllCheckbox = this.findIn("input.select-all");
    const selectedOptionCheckbox = event.currentTarget;
    const allCheckbox = this.findIn("#select-all-elements");
    const pageCheckbox = this.findIn("#select-this-page");
    const optionChecked = selectedOptionCheckbox.checked;

    if (allCheckbox && allCheckbox.id === selectedOptionCheckbox.id) {
      if (pageCheckbox) { pageCheckbox.checked = false; }
    }
    if (pageCheckbox && pageCheckbox.id === selectedOptionCheckbox.id) {
      if (allCheckbox) { allCheckbox.checked = false; }
    }

    this.selections.stages = [];

    this.eachIn(".gift-checkbox", (element) => {
      element.checked = optionChecked;
      this.addOrRemoveStage(element.dataset.stage, optionChecked);
    });

    if (event.currentTarget.id === "select-all-elements" && optionChecked) {
      selectAllCheckbox?.classList.remove("indeterminate");
      this.selections.all = true;
    } else if (event.currentTarget.id === "select-this-page" && optionChecked) {
      selectAllCheckbox?.classList.add("indeterminate");
      this.selections.all = false;
    } else {
      this.selections.yes = [];
      this.selections.all = false;
    }

    if (selectAllCheckbox) { selectAllCheckbox.checked = optionChecked; }
    this.toggleActionButtons();
  }

  onGiftCheckboxChange(event) {
    const checkedStage = event.currentTarget.dataset.stage;
    this.addOrRemoveStage(checkedStage, event.currentTarget.checked);
    this.toggleActionButtons();
  }

  updatePerPage(perPage) {
    $(this.tableTarget).DataTable().page.len(perPage);
  }

  reload(extraParams = {}) {
    ProgressBar.start();

    // select2-initialized filters - .select2("data") needs the jQuery-wrapped element, so this
    // block stays on jQuery.
    const $filterSelects = $(this.element).find(".datatable-moveable-filter, .datatable-filter");
    if ($filterSelects.length > 0) {
      const filter = {};
      $filterSelects.each((_, el) => {
        const $select = $(el);
        const key = $select.attr("name").replace(/(^.*\[|\].*$)/g, "");
        filter[key] = ($select.select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    this.eachIn(".datatable-moveable-text-filter, .datatable-text-filter", (el) => {
      extraParams["filter"] = extraParams["filter"] || {};
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value;
    });

    const params = Object.assign(this.currentParams(), extraParams);

    // `data-source` is read with a plain getAttribute rather than the Values API: it's the same
    // read/write-on-the-table-target convention 15+ other datatable-driving controllers use
    // verbatim (decisions.md D8) - Values API is scoped to the controller element, not a target,
    // so converting just this one would only break the shared convention.
    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    // DataTables' own plugin API, so it stays on jQuery.
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Bracket-notation query string (filter[sender][]=42) into a nested object, the form
  // `$.param()` re-serializes. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }

  // Subdomain::OrdersController#calendar_drawer branches on format via `respond_to`, so it needs
  // both the XHR and full Accept headers.
  openCalendarDrawer(event) {
    this.syncSelectedIds();
    const clickedButton = event.currentTarget;

    const params = {
      order: {
        external_ids: this.selections.yes,
        except_external_ids: this.selections.no,
        type: clickedButton.dataset.type,
      },
    };

    buttonLoading(clickedButton);

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });
    options.body = JSON.stringify(params);

    fetchJson(clickedButton.dataset.url, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        buttonReset(clickedButton);
        return;
      }

      // Server-rendered, our own origin: Subdomain::OrdersController#calendar_drawer renders
      // subdomain/orders/_calendar_drawer (or _calendar_drawer_empty), whose drawer_content
      // partials carry no recipient-entered data - only selection counts, dates and reminder
      // text - and use no raw/html_safe/!=. Confirmed by reading all of them.
      this.findIn(`#${params.order.type}-wrapper`).innerHTML = data.html;
      const calendarDrawer = new OrderCalendarDrawer(this.findIn(`#${params.order.type}-drawer`), params, {
        onSubmitForm: (drawer) => this.submitEdits(drawer),
      });
      calendarDrawer.open();
      buttonReset(clickedButton);
    });
  }

  openReminderDrawer(event) {
    this.syncSelectedIds();
    const clickedButton = event.currentTarget;

    const params = {
      order: {
        external_ids: this.selections.yes,
        except_external_ids: this.selections.no,
        type: clickedButton.dataset.type,
      },
      reminders_view: true,
    };

    buttonLoading(clickedButton);

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });
    options.body = JSON.stringify(params);

    fetchJson(clickedButton.dataset.url, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        buttonReset(clickedButton);
        return;
      }

      // Server-rendered, our own origin - same partial family as openCalendarDrawer above.
      this.findIn(`#${params.order.type}-wrapper`).innerHTML = data.html;
      const reminderDrawer = new OrderCalendarDrawer(this.findIn(`#${params.order.type}-drawer`), params, {
        onSubmitForm: (drawer) => this.submitEdits(drawer),
        onAddReminder: (drawer) => drawer.replaceContent(clickedButton.dataset.url, { use_default_partial: true }),
        onEditReminder: (drawer) => drawer.replaceContent(clickedButton.dataset.url, { use_default_partial: true }),
        onDeleteReminder: (drawer) => {
          drawer.deleteContent(`.reminders .reminder:nth-child(${drawer.params.reminder_index + 1})`, () => this.reload());
        },
      });
      reminderDrawer.open();
      buttonReset(clickedButton);
    });
  }

  // All three PUT targets are bare `render json:` actions with no format negotiation, so only
  // Content-Type is needed.
  submitEdits(drawer) {
    const params = {
      order: {
        external_ids: this.selections.yes,
        except_external_ids: this.selections.no,
        date: drawer.date,
      },
      reminder_id: drawer.params.reminder_id,
      reminder_index: drawer.params.reminder_index,
      range: this.filters.range,
      search: this.filters.search,
      gift_status: this.filters.gift_status,
    };
    drawer.submitting();

    Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);

    const options = RequestHelper.defaultPutOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);

    fetchJson(drawer.url, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        drawer.submitReset();
        return;
      }

      drawer.close();
      this.reload();
      this.resetSelections();

      // Known quirk, left as-is: resetSelections() above already emptied selections.yes/no, so
      // `pluralize` is effectively always true and the toast title is always plural regardless of
      // how many gifts were edited.
      let title = null;
      const icon = "calendar";
      const pluralize = this.selections.all || this.selections.yes.length + this.selections.no.length != 1;

      if (drawer.type() === "next_reminder_at") {
        title = pluralize ? "Reminders have" : "Reminder has";
      } else if (drawer.type() === "expires_on") {
        title = pluralize ? "Expiration dates have" : "Expiration date has";
      } else if (drawer.type() === "scheduled_at") {
        title = pluralize ? "Send dates have" : "Send date has";
      }

      title = title + " been successfully updated.";
      window.ltAlert({ message: data.message, type: "toast-success", title: title, icon: icon });

      if (drawer.type() === "scheduled_at") {
        const editedDate = moment(`${drawer.date} ${this.timezoneOffsetValue}`).utc();
        const picker = $(this.dateRangeInputTarget).data("daterangepicker");
        const pickerStartDate = picker.startDate;
        const pickerEndDate = picker.endDate;

        if (editedDate < pickerStartDate) {
          picker.setStartDate(editedDate);
        } else if (editedDate > pickerEndDate) {
          picker.setEndDate(editedDate);
        }

        this.reload({ range: this.getDatatableRange() });
      }
    });
  }

  // Subdomain::Orders::CancelController#update is also a bare `render json:` action.
  submitCancels(modal) {
    const params = {
      range: this.filters.range,
      search: this.filters.search,
      gift_status: this.filters.gift_status,
      order: {
        external_ids: this.selections.yes,
        except_external_ids: this.selections.no,
      },
    };

    modal.submitting();

    const options = RequestHelper.defaultPutOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);

    fetchJson(modal.url, options).then(({ ok, data }) => {
      if (!ok) {
        console.warn(data);
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        modal.submitReset();
        return;
      }

      modal.close();
      this.reload();
      this.resetSelections();
      window.ltAlert({ message: data.message, type: "toast-success", duration: 2500 });
    });
  }

  openDeleteModal(event) {
    event.preventDefault();
    // A new SimpleModal per click; its handlers accumulate across repeated opens, which is
    // long-standing accepted behaviour here.
    const deleteModal = new SimpleModal(document.getElementById("gifts-delete-modal"), event.currentTarget.dataset.url, (modal) => this.submitCancels(modal));
    deleteModal.open();
    return false;
  }
}
